/**
 * Lightweight Swiper loader
 * Lazily injects the large Swiper bundle only when a slider is present.
 * 
 * Performance optimizations:
 * - Relies on manual triggers and Shopify section events (zero overhead)
 * - Cached customElements checks
 * - Proper cleanup of IntersectionObserver instances
 * - Uses customElements.whenDefined for reliable element registration
 * - Optimized querySelectorAll with combined selector
 * 
 * Configuration:
 * - data-swiper-src: URL to swiper bundle
 * - data-swiper-selector: CSS selector(s) for swiper containers (default: 'swiper-container')
 * - data-swiper-root-margin: IntersectionObserver rootMargin (default: '200px')
 */
(function swiperDeferredLoader() {
    const currentScript = document.currentScript;
    if (!currentScript || !currentScript.dataset) return;
  
    const swiperSrc = currentScript.dataset.swiperSrc;
    const selectors = currentScript.dataset.swiperSelector || 'swiper-container';
    const rootMargin = currentScript.dataset.swiperRootMargin || '200px';
  
    if (!swiperSrc || !selectors) return;
  
    const selectorList = selectors
      .split(',')
      .map((sel) => sel.trim())
      .filter(Boolean);
  
    // Combine selectors for single querySelectorAll call (performance optimization)
    const combinedSelector = selectorList.join(',');
  
    let loadPromise = null;
    let intersectionObservers = new Set();
    let isWatchingDynamicContent = false;
    let isSwiperLoaded = false;
    let evaluateTimer = null;
    let loadErrorCount = 0;
    const MAX_LOAD_RETRIES = 2;
    const LOAD_TIMEOUT = 15000; // 15 seconds
  
    // Cache customElements check result
    const isSwiperRegistered = () => {
      if (isSwiperLoaded) return true;
      isSwiperLoaded = !!window.customElements?.get('swiper-container');
      return isSwiperLoaded;
    };
  
    const querySliders = () => {
      // Use combined selector for better performance (single DOM query)
      return Array.from(document.querySelectorAll(combinedSelector));
    };
  
    const cleanupIntersectionObservers = () => {
      intersectionObservers.forEach(observer => {
        observer.disconnect();
      });
      intersectionObservers.clear();
    };
  
    const stopDynamicWatchers = () => {
      cleanupIntersectionObservers();
      
      if (evaluateTimer) {
        clearTimeout(evaluateTimer);
        evaluateTimer = null;
      }
      
      if (isWatchingDynamicContent) {
        document.removeEventListener('shopify:section:load', deferredEvaluate);
        document.removeEventListener('shopify:section:reorder', deferredEvaluate);
        isWatchingDynamicContent = false;
      }
    };
  
    const loadSwiperBundle = () => {
      if (isSwiperRegistered()) {
        stopDynamicWatchers();
        return Promise.resolve();
      }
  
      if (loadPromise) {
        return loadPromise;
      }
  
      // Prevent infinite retry loop - check error count
      if (loadErrorCount >= MAX_LOAD_RETRIES) {
        console.error(`Swiper failed to load after ${MAX_LOAD_RETRIES} attempts. Please check the script URL.`);
        return Promise.reject(new Error('Swiper load failed after maximum retries'));
      }
  
      loadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = swiperSrc;
        script.async = true;
        script.dataset.swiperBundle = 'true';
        
        let timeoutId = null;
        
        // Add network timeout
        timeoutId = setTimeout(() => {
          loadPromise = null;
          loadErrorCount++;
          script.remove();
          reject(new Error(`Swiper load timeout after ${LOAD_TIMEOUT}ms`));
        }, LOAD_TIMEOUT);
        
        // Use customElements.whenDefined for reliable detection
        const checkRegistration = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          if (isSwiperRegistered()) {
            loadErrorCount = 0; // Reset on success
            stopDynamicWatchers();
            resolve();
          } else {
            // Fallback: check again after a short delay
            setTimeout(() => {
              if (isSwiperRegistered()) {
                loadErrorCount = 0; // Reset on success
                stopDynamicWatchers();
                resolve();
              } else {
                // Still resolve to prevent hanging, but mark as potentially failed
                console.warn('Swiper custom element may not have registered properly');
                resolve();
              }
            }, 200);
          }
        };
        
        script.addEventListener(
          'load',
          () => {
            // Try customElements.whenDefined first (more reliable)
            if (window.customElements && window.customElements.whenDefined) {
              window.customElements.whenDefined('swiper-container')
                .then(() => {
                  isSwiperLoaded = true;
                  checkRegistration();
                })
                .catch(() => {
                  // Fallback if whenDefined fails
                  checkRegistration();
                });
            } else {
              // Fallback for older browsers
              checkRegistration();
            }
          },
          { once: true }
        );
        
        script.addEventListener(
          'error',
          (error) => {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
            loadPromise = null;
            loadErrorCount++;
            
            // Clean up on error
            script.remove();
            stopDynamicWatchers();
            
            // Provide better error message
            const errorMsg = `Failed to load Swiper script from ${swiperSrc}. ${loadErrorCount >= MAX_LOAD_RETRIES ? 'Maximum retries reached.' : 'Retrying...'}`;
            console.error(errorMsg, error);
            
            reject(new Error(errorMsg));
          },
          { once: true }
        );
        
        document.head.appendChild(script);
      });
  
      return loadPromise;
    };
  
    const isElementVisible = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };
  
    const intersectionHandler = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          intersectionObservers.delete(observer);
          loadSwiperBundle();
        }
      });
    };
  
    const watchVisibility = (sliders) => {
      if (!('IntersectionObserver' in window)) {
        loadSwiperBundle();
        return;
      }
  
      const observer = new IntersectionObserver(intersectionHandler, {
        rootMargin,
        threshold: 0.01
      });
      
      intersectionObservers.add(observer);
      sliders.forEach((slider) => observer.observe(slider));
    };
  
    const evaluateSliders = () => {
      // Early exit if already loaded
      if (isSwiperRegistered()) {
        stopDynamicWatchers();
        return true;
      }
      
      const sliders = querySliders();
      if (!sliders.length) {
        return false;
      }
  
      const visible = sliders.some(isElementVisible);
      if (visible) {
        loadSwiperBundle();
      } else {
        watchVisibility(sliders);
      }
      return true;
    };
  
    const deferredEvaluate = () => {
      // Clear any pending evaluation timer
      if (evaluateTimer) {
        clearTimeout(evaluateTimer);
      }
      
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(evaluateSliders, { timeout: 500 });
      } else {
        evaluateTimer = setTimeout(evaluateSliders, 0);
      }
    };
  
    const watchForDynamicSliders = () => {
      // Don't set up watchers if swiper is already loaded
      if (isSwiperRegistered()) {
        return;
      }
      
      if (isWatchingDynamicContent) return;
  
      // Set up Shopify section event listeners (lightweight, zero overhead)
      document.addEventListener('shopify:section:load', deferredEvaluate, { passive: true });
      document.addEventListener('shopify:section:reorder', deferredEvaluate, { passive: true });
      isWatchingDynamicContent = true;
    };
  
    const init = () => {
      if (!evaluateSliders()) {
        // No sliders found on initial load
        // Set up watchers for dynamic content (Shopify section events)
        watchForDynamicSliders();
      } else {
        // Sliders found on initial load
        // Still set up watchers until swiper bundle is actually loaded
        // (in case more sliders are added dynamically via Shopify sections)
        if (!isSwiperRegistered()) {
          watchForDynamicSliders();
        }
      }
    };
  
    // Expose a global function to manually trigger swiper evaluation
    // This can be called when content is dynamically added (e.g., quickshop modal)
    window.triggerSwiperLoad = () => {
      // Early exit if Swiper is already registered
      if (isSwiperRegistered()) {
        return Promise.resolve();
      }
      
      // Prevent duplicate loading - if a load is already in progress, return existing promise
      if (loadPromise) {
        return loadPromise;
      }
      
      const sliders = querySliders();
      if (sliders.length) {
        const visible = sliders.some(isElementVisible);
        if (visible) {
          return loadSwiperBundle();
        } else {
          watchVisibility(sliders);
          return loadSwiperBundle();
        }
      } else {
        // If no sliders found, ensure watchers are active
        watchForDynamicSliders();
        return Promise.resolve();
      }
    };
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  })();